// @flow
import React, { PureComponent } from "react";
import { connect } from "react-redux";
import type { Dispatch } from "redux";
import { get, find } from "lodash";

import type { ChannelUFC } from "@tvg/types/Alerts";
import mediator from "@tvg/mediator";
import ufc from "@tvg/api/ufc";
import { setRaceAlerts } from "@tvg/shared-actions/UserActions";
import RaceAlerts from "@fdr/atomic-ui/_templates/RaceAlerts";
import { onOpenComplianceModal } from "@tvg/sh-lib-account-wallet/src/utils/mobileUtils";
import { isAccountCompliantSelector } from "@tvg/sh-utils/sessionUtils";
import type { SetRaceAlertsActionType } from "@tvg/shared-actions/UserActions";
import { getAccountNumber } from "@urp/store-selectors";

type Props = {
  isNotificationsEnabled: boolean,
  raceAlerts: ChannelUFC[],
  showTopBorder: boolean,
  showBottomBorder: boolean,
  racePostTime: string,
  raceTrackCode: string,
  raceNumber: string,
  tvgRaceId: string,
  showRaceNumber: boolean,
  module: string,
  accountNumber: string,
  disableNotificationTimeout: number,
  dispatch: Dispatch<*>,
  hasTopMargin: boolean,
  isBettingModalOpen?: boolean,
  isRaceAlertsAvailable: boolean,
  isAccountCompliant?: boolean
};

type State = {
  notificationType: string,
  notificationMessage: string
};

export const getRaceAlert = (
  parseChannels: ChannelUFC[],
  raceId: string
  // $FlowFixMe
): ChannelUFC => parseChannels.find((c) => c.entityName === raceId);

export class RaceAlertsComponent extends PureComponent<Props, State> {
  static defaultProps = {
    showTopBorder: false,
    showBottomBorder: false,
    racePostTime: "",
    raceTrackCode: "",
    raceNumber: "",
    tvgRaceId: "",
    snowRaceNumber: false,
    module: "",
    accountNumber: undefined,
    isNotificationsEnabled: true,
    raceAlerts: [],
    hasTopMargin: false,
    disableNotificationTimeout: 3000,
    isBettingModalOpen: false,
    isRaceAlertsAvailable: true,
    isAccountCompliant: false
  };

  constructor(props: Props) {
    super(props);

    this.state = {
      notificationType: "success",
      notificationMessage: ""
    };
  }

  onEnableRaceAlerts = () => {
    if (this.props.isNotificationsEnabled) {
      // request to ufc: add this race to alerts
      ufc
        .setUserAlerts(
          this.props.accountNumber,
          [this.props.tvgRaceId],
          "race",
          ["raceResults", "timeToRace"]
        )
        .then((results) => {
          const updatedRaceAlerts = this.props.raceAlerts.concat(
            get(results, "data[0]")
          );

          this.setState({
            notificationType: "success",
            notificationMessage: `Notifications for race ${this.props.raceNumber} are now on`
          });
          this.props.dispatch(setRaceAlerts(updatedRaceAlerts));
        });
    } else {
      mediator.ios.dispatch({ type: "ASK_PUSH_NOTIFICATION_STATE" });
    }

    // gtm event
    mediator.base.dispatch({
      type: "RACE_ALERTS:TOGGLE",
      payload: {
        toggledOn: true,
        module: this.props.module,
        eventLabel: "Alerts For Race"
      }
    });

    this.hideNotificationMessage();
  };

  onDisableRaceAlerts = () => {
    const updatedChannels = this.props.raceAlerts.filter(
      (channel) => channel.entityName !== this.props.tvgRaceId.toString()
    );

    const alert = getRaceAlert(
      this.props.raceAlerts,
      this.props.tvgRaceId.toString()
    );

    ufc.deleteUserAlert(this.props.accountNumber, [get(alert, "favoriteId")]);
    this.props.dispatch(setRaceAlerts(updatedChannels));

    // gtm event
    mediator.base.dispatch({
      type: "RACE_ALERTS:TOGGLE",
      payload: {
        toggledOn: false,
        module: this.props.module,
        eventLabel: "Alerts For Race"
      }
    });
  };

  isRaceAlertsEnabled = () =>
    !!find(
      this.props.raceAlerts,
      (c) => c.entityName === this.props.tvgRaceId.toString()
    );

  hideNotificationMessage = () => {
    setTimeout(() => {
      this.setState({ notificationMessage: "" });
    }, this.props.disableNotificationTimeout);
  };

  handleToggleClick = () => {
    if (!this.props.isAccountCompliant) {
      onOpenComplianceModal();
      return;
    }

    if (this.isRaceAlertsEnabled()) {
      this.onDisableRaceAlerts();
    } else {
      this.onEnableRaceAlerts();
    }
  };

  render() {
    return (
      this.props.isRaceAlertsAvailable && (
        <RaceAlerts
          hasTopMargin={this.props.hasTopMargin}
          raceNumber={
            this.props.showRaceNumber ? this.props.raceNumber : undefined
          }
          showTopBorder={this.props.showTopBorder}
          showBottomBorder={this.props.showBottomBorder}
          onToggleClick={this.handleToggleClick}
          isOn={this.isRaceAlertsEnabled()}
          notificationType={this.state.notificationType}
          notificationMessage={this.state.notificationMessage}
          isBettingModalOpen={this.props.isBettingModalOpen}
        />
      )
    );
  }
}

export default connect(
  (store) => ({
    isRaceAlertsAvailable: get(
      store,
      "capi.featureToggles.raceAlertsUFC",
      false
    ),
    isNotificationsEnabled: get(store, "ios.isNotificationsEnabled"),
    raceAlerts: get(store, "alerts.raceAlerts", []),
    accountNumber: getAccountNumber(store),
    isAccountCompliant: isAccountCompliantSelector(store),
    isBettingModalOpen: get(store, "RaceProgram.startBetting")
  }),
  (dispatch: Dispatch<SetRaceAlertsActionType>) => ({ dispatch })
)(RaceAlertsComponent);
